import React, { useState, useEffect } from 'react';
import { useFirebase } from '../hooks/useFirebase';
import { Copy, Trash2, Search } from 'lucide-react';
import '../App.css';

const Messages = () => {
  const { messages, deleteMessage, deleteAllMessages, devices } = useFirebase(sessionStorage.getItem('token'));
  const [searchTerm, setSearchTerm] = useState('');
  const [displayLimit, setDisplayLimit] = useState(100);

  // Reset display limit when search term changes
  useEffect(() => {
    setDisplayLimit(100);
  }, [searchTerm]);

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
  };

  const filteredMessages = messages.filter(msg => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (msg.sender || '').toLowerCase().includes(searchLower) ||
      (msg.body || '').toLowerCase().includes(searchLower) ||
      (msg.deviceId || '').toLowerCase().includes(searchLower)
    );
  });

  // Sort messages by timestamp (newest first) and limit display
  const sortedMessages = [...filteredMessages].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  const displayedMessages = sortedMessages.slice(0, displayLimit);
  const hasMoreMessages = sortedMessages.length > displayLimit;

  const loadMore = () => {
    setDisplayLimit(prev => prev + 100);
  };

  return (
    <div className="messages-list">
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>
            All Messages 
            {searchTerm ? (
              <span style={{ fontSize: '16px', fontWeight: '400', color: '#6b7280' }}>
                (Showing {displayedMessages.length} of {filteredMessages.length} matching, {messages.length} total)
              </span>
            ) : (
              <span style={{ fontSize: '16px', fontWeight: '400', color: '#6b7280' }}>
                {displayedMessages.length < filteredMessages.length ? (
                  `(Showing ${displayedMessages.length} of ${filteredMessages.length})`
                ) : (
                  `(${filteredMessages.length})`
                )}
              </span>
            )}
          </h2>
          {messages.length > 0 && (
            <button 
              className="danger-btn" 
              onClick={() => {
                if (window.confirm(`Are you sure you want to delete all ${messages.length} messages? This action cannot be undone.`)) {
                  deleteAllMessages();
                }
              }}
            >
              Delete All
            </button>
          )}
        </div>
        <div className="search-container" style={{ marginBottom: '20px' }}>
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by sender, message, or device ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ width: '100%' }}
          />
        </div>
      </div>
      {displayedMessages.length === 0 ? (
        <div className="loading">
          {searchTerm ? 'No messages found matching your search' : 'No messages found'}
        </div>
      ) : (
        <>
          {displayedMessages.map(msg => {
          const device = devices.find(d => d.id === msg.deviceId);
          return (
            <div key={msg.id} className="message-item">
              <div className="msg-sender">{msg.sender || 'Unknown'}</div>
              <div className="msg-body">{msg.body || ''}</div>
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                fontSize: '12px', 
                color: '#6b7280', 
                marginBottom: '8px',
                flexWrap: 'wrap'
              }}>
                <span style={{ 
                  fontWeight: '500',
                  color: '#3b82f6'
                }}>
                  SIM {msg.simSlot !== null && msg.simSlot !== undefined ? (Number(msg.simSlot) + 1) : 1}
                </span>
                {device?.serial && (
                  <span>Serial: {device.serial}</span>
                )}
                {msg.deviceId && (
                  <span>Device ID: {msg.deviceId.slice(-8)}</span>
                )}
              </div>
              <div className="msg-time">
                {(() => {
                  if (!msg.timestamp) return 'Unknown time';
                  const date = new Date(msg.timestamp);
                  if (isNaN(date.getTime())) return 'Unknown time';
                  try {
                    return date.toLocaleString();
                  } catch (error) {
                    return 'Unknown time';
                  }
                })()}
              </div>
              <div className="msg-actions">
                <button onClick={() => copyText(msg.body)} title="Copy">
                  <Copy size={16} />
                </button>
                <button 
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
                      deleteMessage(msg.id);
                    }
                  }} 
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
          })}
          {hasMoreMessages && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <button 
                onClick={loadMore}
                style={{ 
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#3b82f6';
                }}
              >
                Load More ({sortedMessages.length - displayLimit} remaining)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Messages;
